import { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  ChevronRight,
  ChevronDown,
  Info,
  FileText,
  Gift,
  TrendingUp
} from "lucide-react";
import { supabase } from '@/lib/supabase';
import { useBrands } from '@/lib/BrandsProvider';
import { CreditWarningBanner } from '@/components/credits/CreditWarningBanner';
import { Link, useNavigate } from "react-router-dom";
import ContentGallery from '@/components/content/ContentGallery';
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip,
} from "recharts";
import { Banner } from "@/components/ui/banner";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
// CardContent and AnimatedText imports removed as they're not currently used
import BrandThemeWrapper from '@/components/brand/BrandThemeWrapper';
import NextStepsForSuccess from '@/components/NextStepsForSuccess';
import { GoodiesWidget } from '@/components/GoodiesWidget';
import { TimezoneContext } from '@/components/timezone';
import ConvergenceFeed from '@/components/convergences/ConvergenceFeed';

interface MarketingMessage {
  id: string;
  title: string;
  message: string;
  action_url: string | null;
  action_text: string | null;
  active: boolean | null;
  priority: number | null;
  start_date: string;
  end_date: string | null;
  created_at: string | null;
  updated_at: string | null;
  created_by: string | null;
  updated_by: string | null;
}

// Widget configuration type
type DashboardWidget = {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
  description: string;
  gradient: string;
  iconGradient: string;
  textColor: string;
  subtextColor: string;
  bottomGradient: string;
  actionButtons?: React.ReactNode;
  content: React.ReactNode;
}

// Expandable Widget Component
function ExpandableWidget({ widget, isExpanded, onToggle }: { 
  widget: DashboardWidget; 
  isExpanded: boolean; 
  onToggle: () => void; 
}) {
  return (
    <Card className="bg-white rounded-lg border shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
      {/* Widget Header - Always Visible */}
      <div className={`p-3 md:p-4 border-b bg-gradient-to-r ${widget.gradient} cursor-pointer`}
           onClick={onToggle}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <div className={`w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br ${widget.iconGradient} rounded-full flex items-center justify-center shadow-lg`}>
              <widget.icon className="h-4 w-4 md:h-5 md:w-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className={`text-base md:text-lg font-semibold ${widget.textColor} mb-1`}>
                {widget.title}
              </h3>
              <p className={`text-sm ${widget.subtextColor} mb-2`}>
                {widget.subtitle}
              </p>
              {widget.description && (
                <p className={`text-sm ${widget.subtextColor}`}>
                  {widget.description}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Action buttons when collapsed */}
            {!isExpanded && widget.actionButtons && (
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                {widget.actionButtons}
              </div>
            )}
            {isExpanded ? (
              <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform" />
            ) : (
              <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform" />
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        {isExpanded && (
          <div className="p-6">
            {widget.content}
          </div>
        )}
      </div>
      
      <div className={`h-2 bg-gradient-to-r ${widget.bottomGradient}`} />
    </Card>
  );
}

// Expandable widget wrapper components
// Expandable Content Widget
function ExpandableContentWidget({ isExpanded, onToggle }: { isExpanded: boolean; onToggle: () => void }) {
  const widget: DashboardWidget = {
    id: 'content',
    icon: FileText,
    title: 'Recent Content',
    subtitle: 'Your latest content', 
    description: '',
    gradient: 'from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20',
    iconGradient: 'from-green-500 to-emerald-600',
    textColor: 'text-green-900 dark:text-green-100',
    subtextColor: 'text-green-700 dark:text-green-200',
    bottomGradient: 'from-green-200 via-emerald-200 to-teal-200 dark:from-green-800 dark:via-emerald-800 dark:to-teal-800',
    content: <RecentContentList />
  };

  return <ExpandableWidget widget={widget} isExpanded={isExpanded} onToggle={onToggle} />;
}


// Expandable Content Creation Chart Widget
function ExpandableContentChartWidget({ isExpanded, onToggle }: { isExpanded: boolean; onToggle: () => void }) {
  const widget: DashboardWidget = {
    id: 'content-chart',
    icon: BarChart,
    title: 'Content Produced',
    subtitle: 'Creation activity over time',
    description: 'Track your content production by type and time period',
    gradient: 'from-orange-50 to-red-50 dark:from-orange-950/20 dark:to-red-950/20',
    iconGradient: 'from-orange-500 to-red-600',
    textColor: 'text-orange-900 dark:text-orange-100',
    subtextColor: 'text-orange-700 dark:text-orange-200',
    bottomGradient: 'from-orange-200 via-red-200 to-pink-200 dark:from-orange-800 dark:via-red-800 dark:to-pink-800',
    content: <ContentCreationChart />
  };

  return <ExpandableWidget widget={widget} isExpanded={isExpanded} onToggle={onToggle} />;
}


// Add RecentContentList component
function RecentContentList() {
  const { selectedBrand } = useBrands();
  const [content, setContent] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchRecentContent() {
      if (!selectedBrand) return;

      try {
        setIsLoading(true);
        // Use content_management_view for simplified querying with all necessary fields
        const { data, error } = await supabase
          .from('content_management_view')
          .select('*')
          .eq('brand_id', selectedBrand.id)
          .order('created_at', { ascending: false })
          .limit(12);

        if (error) throw error;

        // Transform data to match ContentGallery expectations
        // The view already has content_json, image_url, video_url, text_preview, etc.
        const transformedContent = (data || []).map((item: any) => {
          return {
            ...item,
            title: item.name,
            platform: item.platform_key,
            // Create campaign object from flat fields
            campaign: item.campaign_name ? {
              id: item.campaign_id,
              name: item.campaign_name,
              image_url: item.campaign_image_url
            } : null,
            campaigns: item.campaign_name ? {
              id: item.campaign_id,
              name: item.campaign_name,
              image_url: item.campaign_image_url
            } : null
          };
        });

        setContent(transformedContent);
      } catch (err) {
        console.error('Error fetching recent content:', err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchRecentContent();
  }, [selectedBrand]);

  return (
    <div className="space-y-4">
        <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h3 className="text-base md:text-lg font-medium">Recent Content</h3>
        </div>
        <Link to="/content" className="text-sm text-green-600 hover:text-green-800 hover:underline font-medium">
          View All
        </Link>
      </div>
      <ContentGallery
        contents={content}
        loading={isLoading}
        emptyMessage="No content found. Create your first content to get started."
        onContentClick={(content) => navigate(`/content/${content.id}`)}
        showCampaign={true}
      />
    </div>
  );
}


// Add ContentCreationChart component
function ContentCreationChart() {
  const { selectedBrand } = useBrands();
  const [contentData, setContentData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeframe, setTimeframe] = useState<'daily' | 'weekly'>('daily');

  // Colors for different content types
  const contentTypeColors = {
    landing_page: "#3b82f6", // Blue
    blog_post: "#10b981",    // Green
    social_post: "#f97316",  // Orange
    ad_group: "#8b5cf6",     // Purple
    javascript_tool: "#f43f5e", // Rose
    email: "#facc15"         // Yellow
  };

  // Helper to format content type for display
  const formatContentType = (type: string) => {
    if (!type) return '';
    return type
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  useEffect(() => {
    async function fetchContentCreationData() {
      if (!selectedBrand) return;

      try {
        setIsLoading(true);
        
        // Get content creation data, grouped by date and content type
        const { data, error } = await supabase
          .from('current_content_versions')
          .select('id, content_type, created_at')
          .eq('brand_id', selectedBrand.id)
          .order('created_at', { ascending: true });

        if (error) throw error;

        // Process the data to group by time periods and content type
        if (data) {
          // Group the data by month/week and content type
          const groupedData = processContentCreationData(data, timeframe);
          setContentData(groupedData);
        }
      } catch (err) {
        console.error('Failed to fetch content creation data:', err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchContentCreationData();
  }, [selectedBrand, timeframe]);

  // Process data to group by time period and content type
  const processContentCreationData = (data: any[], timeframe: 'daily' | 'weekly') => {
    // Create a map to store content counts by period and type
    const periodMap = new Map();
    
    // Process each content item
    data.forEach(item => {
      const date = new Date(item.created_at);
      let periodKey;
      
      if (timeframe === 'weekly') {
        // Group by week (calculate the week's Monday date)
        const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ...
        const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust to calculate days since Monday
        const monday = new Date(date);
        monday.setDate(date.getDate() - daysSinceMonday);
        periodKey = monday.toISOString().split('T')[0]; // YYYY-MM-DD format
      } else {
        // Group by day (YYYY-MM-DD format)
        periodKey = date.toISOString().split('T')[0];
      }
      
      if (!periodMap.has(periodKey)) {
        // Initialize period with all content types set to 0
        periodMap.set(periodKey, {
          period: periodKey,
          landing_page: 0,
          blog_post: 0,
          social_post: 0,
          ad_group: 0,
          javascript_tool: 0,
          email: 0,
          total: 0 // Track total for each period
        });
      }
      
      // Increment the count for this content type
      const periodData = periodMap.get(periodKey);
      const contentType = item.content_type || 'unknown';
      if (periodData[contentType] !== undefined) {
        periodData[contentType]++;
        periodData.total++;
      }
    });
    
    // Convert the map to an array and sort by period
    const result = Array.from(periodMap.values()).sort((a, b) => {
      return a.period.localeCompare(b.period);
    });
    
    // Take only the last 12 periods to keep the chart readable
    return result.slice(-12);
  };

  // Format the period for display
  const formatPeriod = (period: string) => {
    if (timeframe === 'weekly') {
      // Format as MMM DD for weeks
      const date = new Date(period);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else {
      // Format as MMM DD for days
      const date = new Date(period);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  // Get all content types that have data
  const activeContentTypes = useMemo(() => {
    const types = ['landing_page', 'blog_post', 'social_post', 'ad_group', 'javascript_tool', 'email'];
    return types.filter(type => {
      return contentData.some(period => period[type] > 0);
    });
  }, [contentData]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border shadow-sm">
        <div className="p-4 border-b">
          <h2 className="text-base md:text-lg font-semibold">Content Produced</h2>
          <p className="text-sm text-muted-foreground">
            Number of content pieces created by type
          </p>
        </div>
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Tabs
        value={timeframe}
        onValueChange={(value) => setTimeframe(value as 'daily' | 'weekly')}
        className="w-full px-4"
      >
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="daily">Daily</TabsTrigger>
          <TabsTrigger value="weekly">Weekly</TabsTrigger>
        </TabsList>
      </Tabs>
      
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        {/* Increased height container for the chart */}
        <div className="w-full h-[300px] px-4 py-4">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsBarChart 
            data={contentData}
            margin={{ top: 20, right: 10, left: 10, bottom: 20 }}
            barCategoryGap={8}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
            <XAxis
              dataKey="period"
              tickFormatter={formatPeriod}
              fontSize={10}
              tickLine={false}
              axisLine={true}
              height={40}
            />
            <YAxis 
              allowDecimals={false}
              fontSize={10}
              tickLine={false}
              axisLine={false}
              width={30}
            />
            <Legend 
              formatter={(value) => formatContentType(value)}
              iconSize={10}
              fontSize={10}
              wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
            />
            <RechartsTooltip
              formatter={(value, name) => [value, formatContentType(name as string)]}
              labelFormatter={formatPeriod}
            />
            
            {/* Render a stack bar for each content type that has data */}
            {activeContentTypes.map(type => (
              <Bar 
                key={type}
                dataKey={type}
                stackId="stack"
                fill={contentTypeColors[type as keyof typeof contentTypeColors] || "#777"}
                radius={[0, 0, 0, 0]}
                name={type}
              />
            ))}
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const [titleNumber, setTitleNumber] = useState(0);
  const [marketingMessages, setMarketingMessages] = useState<MarketingMessage[]>([]);
  const { selectedBrand } = useBrands();
  const [hiddenMessageIds, setHiddenMessageIds] = useState<Set<string>>(new Set());
  const [nextStepsDismissed, setNextStepsDismissed] = useState(() => {
    return sessionStorage.getItem('nextStepsForSuccessDismissed') === 'true';
  });
  const [expandedWidgets, setExpandedWidgets] = useState<Set<string>>(new Set());

  const titles = useMemo(
    () => ["market trends", "brand growth", "customer engagement", "viral content", "AI insights"],
    []
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (titleNumber === titles.length - 1) {
        setTitleNumber(0);
      } else {
        setTitleNumber(titleNumber + 1);
      }
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [titleNumber, titles]);


  // Fetch marketing messages
  useEffect(() => {
    const fetchMarketingMessages = async () => {
      const session = await supabase.auth.getSession();

      if (!session?.data.session?.user?.id) return;

      // First, get the IDs of messages the user has already viewed/dismissed
      const { data: viewedMessages, error: viewedError } = await supabase
        .schema('marketing_and_customer_success')
        .from('user_message_views')
        .select('message_id')
        .eq('user_id', session.data.session.user.id);

      if (viewedError) {
        console.error('Error fetching viewed messages:', viewedError);
        return;
      }

      // Create a set of viewed message IDs
      const viewedMessageIds = new Set(viewedMessages?.map(vm => vm.message_id) || []);

      // Fetch active messages that haven't been viewed by the user
      const { data: messages, error } = await supabase
        .schema('marketing_and_customer_success')
        .from('marketing_messages')
        .select('*')
        .eq('active', true)
        .order('priority', { ascending: false })
        .order('start_date', { ascending: true })
        .limit(3);

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching marketing messages:', error);
        return;
      }

      if (messages) {
        // Filter out messages that the user has already viewed
        const unviewedMessages = messages.filter(message => !viewedMessageIds.has(message.id));
        setMarketingMessages(unviewedMessages);
      }
    };

    fetchMarketingMessages();
  }, []);

  // Handle message action
  const handleMessageAction = async (messageId: string, actionUrl: string | null) => {
    const session = await supabase.auth.getSession();

    if (!session?.data.session?.user?.id) return;

    try {
      const { error } = await supabase
        .schema('marketing_and_customer_success')
        .from('user_message_views')
        .upsert({
          message_id: messageId,
          viewed_at: new Date().toISOString(),
          action_taken: true,
          action_taken_at: new Date().toISOString(),
          user_id: session.data.session.user.id
        }, {
          onConflict: 'user_id,message_id'
        });

      if (error) throw error;

      if (actionUrl) {
        window.open(actionUrl, '_blank');
      }
    } catch (error) {
      console.error('Error handling message action:', error);
    }
  };

  const hideMessage = async (messageId: string) => {
    const session = await supabase.auth.getSession();

    if (!session?.data.session?.user?.id) return;

    try {
      // Log the message dismissal
      const { error } = await supabase
        .schema('marketing_and_customer_success')
        .from('user_message_views')
        .upsert({
          message_id: messageId,
          viewed_at: new Date().toISOString(),
          action_taken: false,
          user_id: session.data.session.user.id
        }, {
          onConflict: 'user_id,message_id'
        });

      if (error) throw error;

      // Update local state to hide the message
      setHiddenMessageIds(prev => new Set([...prev, messageId]));
    } catch (error) {
      console.error('Error logging message dismissal:', error);
    }
  };

  // Handle dismissing the Next Steps section
  const handleDismissNextSteps = () => {
    sessionStorage.setItem('nextStepsForSuccessDismissed', 'true');
    setNextStepsDismissed(true);
  };

  // Toggle widget expansion
  const toggleWidgetExpansion = (widgetId: string) => {
    setExpandedWidgets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(widgetId)) {
        newSet.delete(widgetId);
      } else {
        newSet.add(widgetId);
      }
      return newSet;
    });
  };

  return (
    <BrandThemeWrapper brandId={selectedBrand?.id || ''} contentType="landing-page">
      <div className="flex flex-col min-w-0">
        {/* Credit Warning Banner - Shows when credits are low */}
        <div className="px-4 sm:px-6 lg:px-8 pt-4">
          <CreditWarningBanner />
        </div>

        {/* Enhanced Dashboard Header */}
        <div className="px-4 sm:px-6 lg:px-4 pt-4 md:pt-4 pb-4 md:pb-6">
          <div className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 rounded-2xl border border-blue-200/50 shadow-sm p-4 md:p-8">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-20 translate-x-20" />
            <div className="relative">
              <div className="flex items-center gap-2 md:gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 md:mb-2">
                    <h1 className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent">
                      {selectedBrand?.brand_name} Dashboard
                    </h1>
                    {/* Tooltip icon - visible on all screen sizes */}
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button 
                            type="button"
                            className="p-1.5 rounded-full hover:bg-blue-100/50 transition-colors flex-shrink-0"
                            aria-label="Dashboard information"
                          >
                            <Info className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-xs p-3">
                          <p className="text-sm leading-relaxed">
                            Monitor your brand's performance and discover opportunities to amplify your market presence through data-driven insights.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Marketing Messages Section */}
        { marketingMessages.length > 0 && (() => {
          const firstMessage = marketingMessages
            .filter(message => !hiddenMessageIds.has(message.id))[0];

          if (!firstMessage) return null;

          return (
            <div className="px-4 sm:px-6 lg:px-8 pb-6">
              <Banner
                key={firstMessage.id}
                show={true}
                icon={<Info className="h-5 w-5 text-green-700" />}
                title={
                  <span>
                    <span className="font-semibold">{firstMessage.title}</span>
                    {" - "}
                    {firstMessage.message}
                  </span>
                }
                action={
                  firstMessage.action_text && firstMessage.action_url
                    ? {
                        label: firstMessage.action_text,
                        onClick: () => handleMessageAction(firstMessage.id, firstMessage.action_url),
                      }
                    : undefined
                }
                onHide={() => hideMessage(firstMessage.id)}
                learnMoreUrl={firstMessage.action_url || undefined}
              />
            </div>
          );
        })()}

        {/* Next Steps for Success - Full Width */}
        {/* 2-Column Layout - Convergence Feed + Widgets */}
        <section className="px-4 sm:px-6 lg:px-8 pb-6">
          <div className="mx-auto">
            {/* Mobile: Stack everything vertically */}
            <div className="lg:hidden space-y-6">
              {/* Convergence Feed - Mobile */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-3 md:p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-violet-50">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                    <h2 className="text-base md:text-lg font-bold text-purple-900">Trending Convergences</h2>
                  </div>
                </div>
                <div className="max-h-[80vh] overflow-y-auto">
                  <ConvergenceFeed />
                </div>
              </div>

              {/* Next Steps - Mobile */}
              {!nextStepsDismissed && (
                <div>
                  <NextStepsForSuccess onDismiss={handleDismissNextSteps} />
                </div>
              )}

              {/* Timezone - Mobile */}
              <div>
                <TimezoneContext
                  placement="header"
                  showCurrentTime={true}
                  compact={false}
                  className="bg-muted/20 rounded-lg"
                />
              </div>

              {/* Widgets - Mobile */}
              <div className="space-y-4">
                <ExpandableGoodiesWidget
                  isExpanded={expandedWidgets.has('goodies')}
                  onToggle={() => toggleWidgetExpansion('goodies')}
                />
                <ExpandableContentChartWidget
                  isExpanded={expandedWidgets.has('content-chart')}
                  onToggle={() => toggleWidgetExpansion('content-chart')}
                />
                <ExpandableContentWidget
                  isExpanded={expandedWidgets.has('content')}
                  onToggle={() => toggleWidgetExpansion('content')}
                />
              </div>
            </div>

            {/* Desktop: 2-Column Layout - Center Feed + Right Sidebar */}
            <div className="hidden lg:grid lg:grid-cols-12 lg:gap-6">
              {/* Center Column - Convergence Feed - 8 columns (wider without left sidebar) */}
              <div className="lg:col-span-8">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-violet-50">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-purple-600" />
                      <h2 className="text-base md:text-lg font-bold text-purple-900">Trending Convergences</h2>
                    </div>
                    <p className="text-sm text-purple-700 mt-1">Your latest topical opportunities</p>
                  </div>
                  <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
                    <ConvergenceFeed />
                  </div>
                </div>
              </div>

              {/* Right Sidebar - Widgets - 4 columns (wider for better widget display) */}
              <div className="lg:col-span-4 space-y-6">
                <div className="sticky top-6 space-y-6">
                  <TimezoneContext
                    placement="header"
                    showCurrentTime={true}
                    compact={false}
                    className="bg-muted/20 rounded-lg"
                  />
                  {!nextStepsDismissed && (
                    <NextStepsForSuccess onDismiss={handleDismissNextSteps} />
                  )}
                  <ExpandableGoodiesWidget
                    isExpanded={expandedWidgets.has('goodies')}
                    onToggle={() => toggleWidgetExpansion('goodies')}
                  />
                  <ExpandableContentChartWidget
                    isExpanded={expandedWidgets.has('content-chart')}
                    onToggle={() => toggleWidgetExpansion('content-chart')}
                  />
                  <ExpandableContentWidget
                    isExpanded={expandedWidgets.has('content')}
                    onToggle={() => toggleWidgetExpansion('content')}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </BrandThemeWrapper>
  );
}

// Expandable Goodies Widget
function ExpandableGoodiesWidget({ isExpanded, onToggle }: { isExpanded: boolean; onToggle: () => void }) {
  const widget: DashboardWidget = {
    id: 'goodies',
    icon: Gift,
    title: 'Goodies & Resources',
    subtitle: 'Access helpful tools, templates, and resources for your brand',
    description: '',
    gradient: 'from-pink-50 to-rose-50 dark:from-pink-950/20 dark:to-rose-950/20',
    iconGradient: 'from-pink-500 to-rose-600',
    textColor: 'text-pink-900 dark:text-pink-100',
    subtextColor: 'text-pink-700 dark:text-pink-200',
    bottomGradient: 'from-pink-200 via-rose-200 to-red-200 dark:from-pink-800 dark:via-rose-800 dark:to-red-800',
    content: <GoodiesWidget />
  };

  return <ExpandableWidget widget={widget} isExpanded={isExpanded} onToggle={onToggle} />;
}

export { Dashboard }; 
